import { useEffect, useRef, useState, useCallback } from "react";
import Chart from "chart.js/auto";
import { Icon, Avatar, EmptyState } from "../../components/Common.jsx";
import { useApp } from "../../store.jsx";
import { useSession } from "../../lib/auth-client";
import { fmtDate, fmtRelative } from "../../lib/dates.js";
import { formatMoney } from "../../lib/money.js";
import TableSkeleton, { CardSkeleton } from "../../components/admin/TableSkeleton.jsx";
import AdminPageBoundary from "../../components/admin/AdminPageBoundary.jsx";
import useInterval from "../../hooks/useInterval.js";
import {
  getOverview,
  getTrialsEnding,
  listAuditLog,
  extendTrial,
  getRevenueSeries,
  getActivitySeries,
  itemsOf,
} from "../../lib/adminApi";

const bentoCard = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 16,
  padding: 18,
  boxShadow:
    "0 1px 0 rgba(255,255,255,.02), 0 4px 16px rgba(0,0,0,.12)",
};

const kpiTile = ({ label, value, sub, icon, accent }) => (
  <div style={{ ...bentoCard }}>
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 1.2,
          color: "var(--text-muted)",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 9,
          display: "grid",
          placeItems: "center",
          background: accent.bg,
          color: accent.fg,
        }}
      >
        <Icon name={icon} size={16} />
      </div>
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, marginTop: 6 }}>{value}</div>
    {sub && (
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
        {sub}
      </div>
    )}
  </div>
);

function OverviewInner() {
  const { toast, goPage, openModal, closeModal } = useApp();
  const { data: session } = useSession();
  const firstName = (session?.user?.name || "Admin").split(" ")[0];

  const [overview, setOverview] = useState(null);
  const [trials, setTrials] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activitySeries, setActivitySeries] = useState([]);
  const [revenueSeries, setRevenueSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  const liveRef = useRef(null);
  const liveChart = useRef(null);
  const heroSparkRef = useRef(null);
  const heroChart = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const ctrl = new AbortController();
    try {
      const [o, t, a, rev, act] = await Promise.all([
        getOverview(),
        getTrialsEnding(),
        listAuditLog("?range=7&limit=20"),
        getRevenueSeries(30),
        getActivitySeries(24),
      ]);
      setOverview(o);
      setTrials(t || []);
      setActivity(itemsOf(a).items.slice(0, 12));
      setRevenueSeries(Array.isArray(rev) ? rev : []);
      setActivitySeries(Array.isArray(act?.series) ? act.series : []);
    } catch (e) {
      if (!ctrl.signal.aborted) toast(e.message || "Failed to load overview", "error");
    } finally {
      setLoading(false);
    }
    return () => ctrl.abort();
  }, [toast]);

  useEffect(() => { load(); }, [load]);
  useInterval(load, 60_000);

  // Live activity — wired to /admin/metrics/activity (per-hour audit + report events).
  useEffect(() => {
    if (!liveRef.current) return;
    if (liveChart.current) liveChart.current.destroy();
    const data = activitySeries.length
      ? activitySeries.map((p) => p.count)
      : Array(12).fill(0);
    const labels = activitySeries.length
      ? activitySeries.map((p) => new Date(p.ts).getHours() + "h")
      : Array(12).fill("");

    const grad = (ctx) => {
      const g = ctx.chart.ctx.createLinearGradient(0, 0, 0, 220);
      g.addColorStop(0, "rgba(139,92,246,.55)");
      g.addColorStop(1, "rgba(139,92,246,0)");
      return g;
    };

    liveChart.current = new Chart(liveRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Events / hour",
            data,
            borderColor: "#8B5CF6",
            backgroundColor: grad,
            fill: true,
            tension: 0.4,
            borderWidth: 2.2,
            pointRadius: 0,
            pointHoverRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600 },
        plugins: { legend: { display: false } },
        scales: {
          x: { display: false },
          y: { display: false, beginAtZero: true },
        },
      },
    });

    return () => liveChart.current?.destroy();
  }, [activitySeries]);

  useEffect(() => {
    if (!heroSparkRef.current) return;
    if (heroChart.current) heroChart.current.destroy();
    const labels = revenueSeries.map((r) => r.date);
    const values = revenueSeries.map((r) => (r.amount || 0) / 100);
    const fallback = !values.length ? Array(8).fill(0) : values;

    heroChart.current = new Chart(heroSparkRef.current, {
      type: "line",
      data: {
        labels: labels.length ? labels : fallback.map(() => ""),
        datasets: [
          {
            data: fallback,
            borderColor: "#7c3aed",
            backgroundColor: "rgba(124,58,237,.18)",
            fill: true,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 800 },
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false } },
      },
    });
    return () => heroChart.current?.destroy();
  }, [revenueSeries]);

  const onExtend = (id, name) => {
    let days = 14;
    let reason = "";
    openModal(
      <>
        <div className="modal-head">
          <h3>Extend trial for {name}</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="field">
              <label>Days (1-60)</label>
              <input
                className="input"
                type="number"
                min={1}
                max={60}
                defaultValue={days}
                onChange={(e) => { days = Number(e.target.value); }}
              />
            </div>
          </div>
          <div className="field">
            <label>Reason (≥4 chars)</label>
            <textarea className="input" rows={3} onChange={(e) => { reason = e.target.value; }} />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={async () => {
            if (!days || days < 1) return toast("Days must be 1-60", "error");
            if (!reason || reason.trim().length < 4) return toast("Reason required", "error");
            try {
              await extendTrial(id, days, reason);
              toast(`Trial extended by ${days} days`, "success");
              closeModal();
              setTrials((p) => p.filter((c) => c.id !== id));
            } catch (e) { toast(e.message, "error"); }
          }}>
            <Icon name="calendar-plus" size={14} /> Extend
          </button>
        </div>
      </>,
    );
  };

  const accent = {
    primary: { bg: "rgba(139,92,246,.15)", fg: "#8B5CF6" },
    success: { bg: "rgba(16,185,129,.15)", fg: "#10B981" },
    warning: { bg: "rgba(245,158,11,.15)", fg: "#F59E0B" },
    error:   { bg: "rgba(239,68,68,.15)",  fg: "#EF4444" },
    muted:   { bg: "rgba(148,163,184,.15)", fg: "#94A3B8" },
  };

  const renderKpis = () => {
    if (!overview) {
      return Array.from({ length: 5 }).map((_, i) => (
        <CardSkeleton key={i} height={90} />
      ));
    }
    return [
      { label: "Active Tenants", value: overview.activeCompanies, sub: `${overview.totalCompanies || 0} total`, icon: "building", accent: accent.success },
      { label: "Active Subscriptions", value: overview.activeSubscriptions, sub: "Paying customers", icon: "credit-card", accent: accent.primary },
      { label: "Trialing", value: overview.trialing, sub: "Active free trials", icon: "clock", accent: accent.warning },
      { label: "Past Due", value: overview.pastDue, sub: "Needs attention", icon: "alert-triangle", accent: accent.error },
      { label: "Churn 30d", value: overview.churn30d, sub: "Canceled this month", icon: "user-minus", accent: accent.muted },
      { label: "Revenue 30d", value: formatMoney(overview.revenue30d), sub: "Paid invoices", icon: "trending-up", accent: accent.primary },
    ].map((t) => <div key={t.label}>{kpiTile(t)}</div>);
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Welcome back, {firstName}</h1>
          <p className="page-sub">
            Live platform health · {fmtDate(new Date())}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn btn-ghost btn-sm" onClick={load}>
            <Icon name="refresh-cw" size={14} /> Refresh
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => goPage("adminCompanies")}
          >
            <Icon name="building" size={14} /> Companies
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => goPage("adminAuditLog")}
          >
            <Icon name="file-text" size={14} /> Audit Log
          </button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            ...bentoCard,
            background:
              "linear-gradient(135deg, rgba(139,92,246,0.12) 0%, rgba(99,102,241,0.08) 60%, rgba(59,130,246,0.05) 100%)",
            border: "1px solid rgba(139,92,246,0.28)",
            minHeight: 230,
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 1.2,
                fontWeight: 600,
                color: "var(--text-muted)",
              }}
            >
              Monthly Recurring Revenue
            </div>
            <div
              style={{
                fontSize: 48,
                fontWeight: 800,
                lineHeight: 1.05,
                marginTop: 6,
                background:
                  "linear-gradient(135deg,#7c3aed,#4f46e5,#2563eb)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {overview ? formatMoney(overview.mrr) : "—"}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
              {overview ? `ARR ${formatMoney(overview.arr)}` : "Loading…"}
              {overview ? ` · ${overview.activeSubscriptions} active subs` : ""}
            </div>
          </div>
          <div style={{ height: 64, marginTop: 8 }}>
            <canvas ref={heroSparkRef} />
          </div>
        </div>

        <div style={{ ...bentoCard, position: "relative", overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                  color: "var(--text-muted)",
                  fontWeight: 600,
                }}
              >
                Live Activity (24h)
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>
                Audit + report events
              </div>
            </div>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11,
                color: "#10B981",
                fontWeight: 600,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#10B981",
                  boxShadow: "0 0 0 4px rgba(16,185,129,0.18)",
                  animation: "pulse 1.4s ease-in-out infinite",
                }}
              />
              LIVE
            </span>
          </div>
          <div style={{ height: 150, marginTop: 8 }}>
            <canvas ref={liveRef} />
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        {renderKpis()}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.3fr",
          gap: 16,
        }}
      >
        <div style={bentoCard}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                Trials ending in 7 days
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                Reach out before they churn
              </div>
            </div>
            <span className="badge badge-warning">{trials.length}</span>
          </div>
          {loading ? (
            <TableSkeleton columns={2} rows={3} />
          ) : trials.length === 0 ? (
            <div className="empty-state" style={{ padding: 24 }}>
              <div className="ico">
                <Icon name="check-circle" size={26} />
              </div>
              <h3 style={{ fontSize: 14 }}>All trials healthy</h3>
              <p style={{ fontSize: 12 }}>No trials expiring this week.</p>
            </div>
          ) : (
            trials.map((t) => (
              <div key={t.id} className="activity-row">
                <Avatar name={t.name} size="sm" seed={t.name?.length || 0} />
                <div className="activity-text">
                  <div className="activity-title" style={{ fontWeight: 600 }}>
                    {t.name}
                  </div>
                  <div className="activity-meta">
                    ends {fmtDate(t.trialEnd)}
                  </div>
                </div>
                <button
                  className="btn btn-ghost btn-sm activity-view"
                  onClick={() => onExtend(t.id, t.name)}
                >
                  <Icon name="calendar-plus" size={14} /> Extend
                </button>
              </div>
            ))
          )}
        </div>

        <div style={bentoCard}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Recent activity</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                Last 7 days of admin actions
              </div>
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => goPage("adminAuditLog")}
            >
              View all →
            </button>
          </div>
          {loading ? (
            <TableSkeleton columns={2} rows={5} />
          ) : activity.length === 0 ? (
            <EmptyState
              title="No activity yet"
              desc="Admin actions will appear here."
              icon="activity"
            />
          ) : (
            activity.map((a) => (
              <div
                key={a.id}
                style={{
                  display: "flex",
                  gap: 12,
                  padding: "10px 0",
                  alignItems: "center",
                  borderLeft: "2px solid var(--border)",
                  paddingLeft: 16,
                  marginLeft: 6,
                  position: "relative",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    left: -6,
                    top: 18,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "var(--primary)",
                    boxShadow: "0 0 0 3px var(--surface)",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {a.actor?.email || "system"}{" "}
                    <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>
                      performed
                    </span>{" "}
                    <span style={{ color: "var(--primary-2)", fontWeight: 600 }}>
                      {a.action}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-muted)",
                      marginTop: 2,
                    }}
                  >
                    on {a.targetType} · {fmtRelative(a.createdAt)}
                  </div>
                </div>
                <span className="badge badge-muted">{a.targetType}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.6; }
        }
      `}</style>
    </section>
  );
}

export default function AdminOverview() {
  return (
    <AdminPageBoundary>
      <OverviewInner />
    </AdminPageBoundary>
  );
}
